/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving saes.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';
import * as React from 'react';
import { BoundActionCreator, OmitFirstArg } from '@ngt/online-patient-management-ui';

/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IIndividualSaesState {
    saes: Array<Dtos.Sae<Dtos.SaeForm, Dtos.Patient>> | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
};

export interface ISaesState {
    byContext: Record<string, IIndividualSaesState>;
};

export interface ISaesStore {
    saes: ISaesState
}

export interface IUseSaeActions {
    load: BoundActionCreator<OmitFirstArg<typeof SaesActions.load>>;
    clear: BoundActionCreator<OmitFirstArg<typeof SaesActions.clear>>;
}

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const initialIndividualSaesState: IIndividualSaesState = {
    saes: null,
    loadState: {
        state: RequestState.None
    }
};

const initialSaesState: ISaesState = {
    byContext: { }
}

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class SaesReducer extends ImmerReducer<ISaesState> {
    public load(institutionCode?: string) {
        const context = `${institutionCode}`;

        if (!this.draftState.byContext[context]) {
            this.draftState.byContext[context] = { ...initialIndividualSaesState };
        }

        this.draftState.byContext[context].loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(institutionCode?: string, saes?: Array<Dtos.Sae<Dtos.SaeForm, Dtos.Patient>>) {
        const context = `${institutionCode}`;

        if (!this.draftState.byContext[context]) {
            this.draftState.byContext[context] = { ...initialIndividualSaesState };
        }

        this.draftState.byContext[context].saes = saes ? saes : null;

        this.draftState.byContext[context].loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(institutionCode?: string, responseStatus?: Dtos.ResponseStatus) {
        const context = `${institutionCode}`;

        if (!this.draftState.byContext[context]) {
            this.draftState.byContext[context] = { ...initialIndividualSaesState };
        }

        this.draftState.byContext[context].loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear(institutionCode?: string) {
        const context = `${institutionCode}`;

        if (this.draftState.byContext[context]) {
            delete this.draftState.byContext[context];
        }
    }

    public clearAll() {
        this.draftState = { ...initialSaesState };
    }
};

export const SaesActions = createActionCreators(SaesReducer);
const reducer = createReducerFunction(SaesReducer, initialSaesState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const SaesApi = {
    load: () => {
        return client.get(new Dtos.SaeGetCollection());
    },
    loadByInstitutionCode: (institutionCode?: string) => {
        const request: Dtos.SaeGetCollectionByInstitutionCode = new Dtos.SaeGetCollectionByInstitutionCode();
        request.institutionCode = institutionCode!!;

        return client.get(request);
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const SaesLogic = {
    load: createLogic<ISaesStore, {}, undefined, string, ReturnType<typeof SaesActions.load>>({
        type: SaesActions.load.type,
        process: async ({ action }, dispatch, done) => {
            const institutionCode = action.payload as unknown as string;

            try {
                let response: Dtos.SaeCollectionResponse | null = null;

                if (institutionCode) {
                    response = await SaesApi.loadByInstitutionCode(institutionCode);
                } else {
                    response = await SaesApi.load();
                }

                dispatch(SaesActions.loadSuccess(
                    institutionCode,
                    response.saes
                ));
            }
            catch (error: any) {
                dispatch(SaesActions.loadFailure(
                    institutionCode,
                    error ? error.responseStatus : undefined
                ));
            }

            done();
        }
    })
};

const logics = [
    SaesLogic.load
];

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useSaesSelector: TypedUseSelectorHook<ISaesStore> = useSelector;

export const saesSelectors = {
    saes: (state: ISaesStore, institutionCode?: string) => {
        const context = `${institutionCode}`;

        return state.saes?.byContext[context]?.saes ?? initialIndividualSaesState.saes;
    },
    loadState: (state: ISaesStore, institutionCode?: string) => {
        const context = `${institutionCode}`;

        return state.saes?.byContext[context]?.loadState ?? initialIndividualSaesState.loadState;
    }
};

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useSaes = (institutionCode?: string): [
    Array<Dtos.Sae<Dtos.SaeForm, Dtos.Patient>> | null,
    IRequestState<Dtos.ResponseStatus>,
    IUseSaeActions
] => {
    const dispatch = useDispatch();

    const actions = React.useMemo(() => {
        const load = () => SaesActions.load(institutionCode);
        load.type = SaesActions.load.type;

        const clear = () => SaesActions.clear(institutionCode);
        clear.type = SaesActions.clear.type;

        return bindActionCreators({
            load,
            clear
        }, dispatch);
    }, [institutionCode, dispatch]);

    const saeSelector = React.useCallback((state: ISaesStore) => {
        return saesSelectors.saes(state, institutionCode)
    }, [institutionCode]);

    const loadStateSelector = React.useCallback((state: ISaesStore) => {
        return saesSelectors.loadState(state, institutionCode)
    }, [institutionCode]);

    const saes = useSaesSelector(saeSelector);

    const loadState = useSaesSelector(loadStateSelector);

    return [
        saes,
        loadState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('saes', reducer as any, logics as any);